import type { ActionTicksOut, SessionResultOut } from "../contract/api";
import { captureAnalytics } from "../analytics";
import { t } from "../contract/strings";
import { ChecklistIcon, LinkIcon, cx, speakerName } from "../ui";
import type { ActionTicks } from "./result-actions-state";
import { LABEL, dueLabel, fmtMinSec, type Commitment, type DueLabel } from "./result-model";

/** How a row's timing is drawn: the accent, the warning tone past due, muted once done. */
const DUE_TONE: Record<DueLabel["tone"], string> = {
  due: "font-bold text-accent",
  overdue: "font-bold text-warn-ink",
  muted: "text-ink-soft",
};

/** One decision or action: a commitment, its owner, and the key its tick is saved under. */
export interface ActionItem extends Commitment {
  /** The server's `action_key` (#786); null on a commitment that has none, never ticked. */
  key: string | null;
}

/**
 * Every commitment of the meeting, in card order, each with its owner (the commitment's
 * own, else the card's speaker) and the key the server put on it (#786). The web never
 * computes one: the result endpoint serves every commitment with its key.
 */
export function resultActions(result: SessionResultOut): ActionItem[] {
  return (result.cards ?? []).flatMap((card) =>
    (card.commitments ?? []).map((c) => ({
      ...c,
      speaker: c.owner || card.speaker,
      key: c.action_key || null,
    })),
  );
}

/** A message key and its placeholders, as the core's rows carry them. */
interface RowMessage {
  key: string;
  params: Record<string, string>;
  nested: Record<string, never>;
}

/** One action as the core's `action_rows` gives it (#786). */
export interface ActionRowModel {
  /** The server's key; null on a commitment without one (it cannot be ticked). */
  key: string | null;
  text: string;
  owner: string;
  due: string | null;
  due_date?: string;
  source_start: number | null;
  done: boolean;
  done_by?: RowMessage;
}

/**
 * The Actions card's rows with the meeting's saved ticks: the core's `action_rows`
 * (`core/crates/mf-present/src/actions.rs`), field for field, checked against the shared
 * fixtures in `contract/fixtures/present/actions/`. A tick whose key this result does not
 * have is ignored, so the counter counts only the rows on screen.
 */
export function actionRows(
  result: SessionResultOut,
  saved: ActionTicksOut,
): { done: number; total: number; counter: RowMessage; rows: ActionRowModel[] } {
  const byKey = new Map(saved.ticks.map((tick) => [tick.action_key, tick]));
  const rows = resultActions(result).map((item): ActionRowModel => {
    const tick = item.key ? byKey.get(item.key) : undefined;
    const done = Boolean(tick?.done);
    const row: ActionRowModel = {
      key: item.key,
      text: item.text,
      owner: item.speaker,
      due: item.due ?? null,
      source_start: item.source_start ?? null,
      done,
    };
    if (item.due_date) row.due_date = item.due_date;
    if (done && tick?.done_by) {
      row.done_by = { key: "result.action_done_by", params: { name: tick.done_by }, nested: {} };
    }
    return row;
  });
  const done = rows.filter((row) => row.done).length;
  return {
    done,
    total: rows.length,
    counter: {
      key: "result.actions_done",
      params: { done: String(done), total: String(rows.length) },
      nested: {},
    },
    rows,
  };
}

/**
 * The small link icon in a row's bottom-right corner that jumps to a moment in the
 * transcript. Quiet and small; the invisible 44-unit box around it takes the tap without
 * making the row any taller. Its row needs `relative` and room on the right (`pr-8`).
 */
export function MomentLink({
  at,
  onJump,
  ...data
}: {
  at: number;
  onJump: (time: number) => void;
} & { [key: `data-${string}`]: boolean }) {
  const label = t("result.jump_to_moment_aria", { time: fmtMinSec(at) });
  return (
    <button
      type="button"
      {...data}
      onClick={() => onJump(at)}
      aria-label={label}
      title={label}
      className={cx(
        "absolute bottom-2 right-2 inline-flex items-center justify-center p-0 bg-transparent border-none rounded-control cursor-pointer",
        "text-ink-soft hover:text-accent focus-visible:text-accent transition-colors",
        "after:absolute after:left-1/2 after:top-1/2 after:size-11 after:-translate-x-1/2 after:-translate-y-1/2 after:content-['']",
      )}
    >
      <LinkIcon className="size-icon-xs" />
    </button>
  );
}

/**
 * Quick's "Actions" card (#766): every commitment as a row with a real checkbox, its
 * owner, its due date and -- when the summariser placed it -- a jump to the moment it was
 * said. Ticks are the meeting's (#786): everyone in it sees the same, with who ticked.
 */
export function ActionsCard({
  result,
  ticks,
  onJump,
  groupBy,
}: {
  result: SessionResultOut;
  /** The meeting's shared ticks, held by the screen so both tabs show one state. */
  ticks: ActionTicks;
  onJump: (time: number) => void;
  /**
   * `person` lays the same rows out under each owner, in card order -- Detailed's
   * "commitments on each side" (#774). The rows, their ticks and the counter are
   * Quick's own either way.
   */
  groupBy?: "person";
}) {
  const items = resultActions(result);
  // The reader's own today, for "Due tomorrow" and "Overdue" (#772).
  const now = new Date();

  if (!items.length) return null;

  const groups: Array<{ speaker: string | null; items: ActionItem[] }> = [];
  if (groupBy === "person") {
    for (const item of items) {
      const group = groups.find((g) => g.speaker === item.speaker);
      if (group) group.items.push(item);
      else groups.push({ speaker: item.speaker, items: [item] });
    }
  } else {
    groups.push({ speaker: null, items });
  }

  // The counter follows the meeting's state: only this result's actions count.
  const doneCount = items.filter((item) => item.key && ticks.view.get(item.key)?.done).length;

  return (
    <section
      data-actions-card
      className="p-4 bg-surface-2 border border-line rounded-card flex flex-col gap-stack"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-tight min-w-0">
          <ChecklistIcon className="size-icon-xs text-accent flex-none" />
          <span className={cx(LABEL, "text-label text-ink-soft")}>{t("result.actions_heading")}</span>
        </span>
        <span aria-live="polite" className={cx(LABEL, "flex-none text-count text-ink-soft tabular-nums")}>
          {t("result.actions_done", { done: doneCount, total: items.length })}
        </span>
      </div>
      {ticks.failed ? (
        <p data-actions-failed role="status" className="m-0 text-count text-warn-ink">
          {t("result.action_save_failed")}
        </p>
      ) : null}

      {groups.map((group) => (
        <div key={group.speaker ?? ""} data-actions-group={group.speaker ?? undefined} className="flex flex-col gap-tight">
          {group.speaker !== null ? (
            <h4 className="m-0 font-title text-note font-semibold text-ink">{speakerName(group.speaker)}</h4>
          ) : null}
      <ul data-actions className="m-0 p-0 list-none flex flex-col gap-tight">
        {group.items.map((item, index) => {
          const key = item.key;
          const tick = key ? ticks.view.get(key) : undefined;
          const checked = Boolean(tick?.done);
          const doneBy = checked ? tick?.doneBy : null;
          const at = typeof item.source_start === "number" ? item.source_start : null;
          const due = dueLabel(item, { now, done: checked });
          return (
            // The row's card is the item; the jump is its sibling of the label, not inside
            // it, so pressing the jump can never tick the box.
            <li key={key ?? `unkeyed-${index}`} className="relative bg-surface rounded-control">
              <label
                data-action
                className={cx(
                  // The checkbox centres on the whole text block (the text and its meta
                  // line), however many lines the text wraps to.
                  "flex items-center gap-tight min-h-11 p-2 rounded-control cursor-pointer hover:bg-accent/5 transition-colors",
                  at !== null && "pr-8",
                )}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={!key}
                  onChange={() => {
                    if (key) {
                      captureAnalytics("result_action_toggled", {
                        state: checked ? "unchecked" : "checked",
                      });
                      ticks.toggle(key);
                    }
                  }}
                  aria-label={t("result.action_mark_done", { text: item.text })}
                  className="flex-none size-icon-xs accent-accent cursor-pointer"
                />
                <span className="min-w-0 flex flex-col gap-nudge">
                  <span
                    data-action-text
                    lang={result.language ?? undefined}
                    className={cx(
                      "text-note text-ink transition-opacity",
                      checked && "line-through opacity-60",
                    )}
                  >
                    {item.text}
                  </span>
                  <span className="flex flex-wrap items-center gap-x-tight gap-y-nudge text-count text-ink-soft">
                    <span>{t("result.action_owner", { name: speakerName(item.speaker) })}</span>
                    {due ? (
                      <>
                        <span aria-hidden="true">•</span>
                        <span data-action-due={due.tone} className={DUE_TONE[due.tone]}>
                          {due.text}
                        </span>
                      </>
                    ) : null}
                    {doneBy ? (
                      <>
                        <span aria-hidden="true">•</span>
                        <span data-action-done-by>{t("result.action_done_by", { name: doneBy })}</span>
                      </>
                    ) : null}
                  </span>
                </span>
              </label>
              {at !== null ? <MomentLink data-action-jump at={at} onJump={onJump} /> : null}
            </li>
          );
        })}
      </ul>
        </div>
      ))}
    </section>
  );
}
